import { flexRender } from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@repo/core/components/shadcn/table";
import { Checkbox } from "@repo/core/components/shadcn/checkbox";
import { Button } from "@repo/core/components/shadcn/button";
import { cn } from "@repo/core/utils/cn";
import { Text } from "@repo/core/components/core/Text";
import { DataTableProps, TableConvertedData, TableHeaderData } from "./types";
import { View } from "@gluestack-ui/themed";

export function DataTable<TData>({
  table,
  enableRowSelection
}: DataTableProps<TData>) {
  return (
    <div className="m-2 space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {enableRowSelection && (
                  <TableHead className="w-[70px]">
                    <div className="flex h-full flex-col items-center justify-center">
                      <div className="flex h-8 items-center justify-center">
                        <Checkbox
                          checked={table.getIsAllPageRowsSelected()}
                          onCheckedChange={(value) =>
                            table.toggleAllPageRowsSelected(!!value)
                          }
                          aria-label="Select all"
                        />
                      </div>
                    </div>
                  </TableHead>
                )}
                {headerGroup.headers.map((header) => {
                  const containerClass = `${(header.column.columnDef.meta as TableHeaderData["styles"])?.containerClass}`;
                  return (
                    <TableHead key={header.id} className={"h-8 px-2"}>
                      <div className="flex flex-col">
                        <div
                          className={cn(
                            "flex h-4 items-center",
                            containerClass
                          )}
                        >
                          {header.column.getCanSort() ? (
                            <Button
                              variant="ghost"
                              onClick={() =>
                                header.column.toggleSorting(
                                  header.column.getIsSorted() === "asc"
                                )
                              }
                              className="-ml-2 h-8 px-2 py-1"
                            >
                              <span className="mr-2">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </span>
                            </Button>
                          ) : (
                            !header.isPlaceholder && (
                              <div className="flex h-8 items-center px-2 py-1">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.map((row) => {
              const className = (row.original as TableConvertedData).meta
                ?.className;

              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={className}
                >
                  {enableRowSelection && (
                    <TableCell className="w-[70px]">
                      <div className="flex justify-center">
                        <Checkbox
                          checked={row.getIsSelected()}
                          onCheckedChange={(value) => {
                            row.toggleSelected(!!value);
                          }}
                          aria-label="Select row"
                        />
                      </div>
                    </TableCell>
                  )}
                  {row.getVisibleCells().map((cell) => {
                    const styles = (row.original as TableConvertedData).meta
                      .valuesStyles?.[cell.column.id];
                    return (
                      <TableCell
                        key={cell.id}
                        className={styles?.containerClass}
                      >
                        <div
                          className={cn(
                            "flex flex-row items-center justify-center gap-1",
                            styles?.containerClass
                          )}
                        >
                          {styles?.showIndicator && (
                            <View
                              bg={styles?.indicatorColor}
                              h="$5"
                              w="$5"
                              borderRadius={4}
                            />
                          )}
                          <Text
                            fontSize="$sm"
                            color={styles?.color || "$secondary500"}
                            fontWeight={styles?.fontWeight || 400}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Text>
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            }) || (
              <TableRow>
                <TableCell
                  colSpan={table.getAllColumns().length}
                  className="h-24 text-center"
                >
                  No data found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
